import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons'
import { Button, Modal, Spin, Typography } from 'antd'
import { ImageViewer } from 'features/files/components/ImageViewer'
import { PdfViewer } from 'features/files/components/PdfViewer'
import { useDocumentSignedUrls } from 'features/files/hooks/useDocuments'
import { Document } from 'features/files/types'
import * as React from 'react'
import styled from 'styled-components'

const { Text } = Typography

export interface DocumentPreviewProps {
  document: Document | null
  className?: string
}

const Container = styled.div`
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const TitleContainer = styled.div`
  flex: 1;
`

const UnsupportedState = styled.div`
  text-align: center;
  padding: 1.25rem;
  color: #999;
`

const FullscreenPdfViewer = styled(PdfViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const FullscreenImageViewer = styled(ImageViewer)`
  width: 100%;
  height: calc(100vh - 7.5rem);
`

const ModalTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LoadingContainer = styled.div`
  text-align: center;
  padding: 1.25rem;
`

interface FullscreenModalProps {
  document: Document
  signedUrl: string
  visible: boolean
  onClose: () => void
  isPdf: boolean
  isImage: boolean
}

const FullscreenModal: React.FC<FullscreenModalProps> = ({
  document,
  signedUrl,
  visible,
  onClose,
  isPdf,
  isImage,
}) => (
  <Modal
    open={visible}
    onCancel={onClose}
    footer={null}
    width='95vw'
    style={{ top: '1.25rem' }}
    bodyStyle={{ padding: 0, height: '90vh' }}
    closable={false}
    title={
      <ModalTitleContainer>
        <span>{document.name}</span>
        <Button
          type='text'
          icon={<FullscreenExitOutlined />}
          onClick={onClose}
          title='Exit fullscreen'
        />
      </ModalTitleContainer>
    }>
    {isPdf && <FullscreenPdfViewer url={signedUrl} showPagination={true} />}
    {isImage && <FullscreenImageViewer url={signedUrl} height={1000} />}
  </Modal>
)

export const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  document,
  className,
}) => {
  const { signedUrl, isLoading, isPdf, isImage } =
    useDocumentSignedUrls(document)
  const [isFullscreen, setIsFullscreen] = React.useState(false)

  if (!document) {
    return (
      <Container className={className}>
        <Text
          strong
          style={{ fontSize: 16, marginBottom: 12, display: 'block' }}>
          Document Preview
        </Text>
        <UnsupportedState>
          <Text type='secondary'>Select a document to preview</Text>
        </UnsupportedState>
      </Container>
    )
  }

  const canPreview = isPdf || isImage
  const previewTitle = isPdf
    ? 'PDF Preview'
    : isImage
      ? 'Image Preview'
      : 'File Preview'

  return (
    <>
      <Container className={className}>
        <HeaderContainer>
          <TitleContainer>
            <Text strong style={{ fontSize: 16, display: 'block' }}>
              {previewTitle}
            </Text>
          </TitleContainer>
          {canPreview && signedUrl && (
            <Button
              type='text'
              icon={<FullscreenOutlined />}
              onClick={() => setIsFullscreen(true)}
              title='Open in fullscreen'
            />
          )}
        </HeaderContainer>

        {isPdf ? (
          signedUrl ? (
            <PdfViewer url={signedUrl} />
          ) : isLoading ? (
            <LoadingContainer>
              <Spin tip='Loading PDF preview...' />
            </LoadingContainer>
          ) : (
            <UnsupportedState>
              <Text type='secondary'>Unable to load PDF preview</Text>
            </UnsupportedState>
          )
        ) : isImage ? (
          signedUrl ? (
            <ImageViewer url={signedUrl} />
          ) : isLoading ? (
            <LoadingContainer>
              <Spin tip='Loading image preview...' />
            </LoadingContainer>
          ) : (
            <UnsupportedState>
              <Text type='secondary'>Unable to load image preview</Text>
            </UnsupportedState>
          )
        ) : (
          <UnsupportedState>
            <Text type='secondary'>
              Preview not available for this file type
            </Text>
          </UnsupportedState>
        )}
      </Container>

      {document && signedUrl && canPreview && (
        <FullscreenModal
          document={document}
          signedUrl={signedUrl}
          visible={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          isPdf={Boolean(isPdf)}
          isImage={Boolean(isImage)}
        />
      )}
    </>
  )
}
